import { Code2, FileArchive, FileInput, Users, Cpu, HardDrive, Activity } from 'lucide-react';

const About = () => {
  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">About File Compressor</h1>
        
        <div className="prose dark:prose-invert max-w-none">
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            File Compressor is a web application that allows you to compress and decompress files using modern compression algorithms.
            The application is built with React and runs entirely in your browser, ensuring your files never leave your device.
          </p>
          
          <h2 className="text-xl font-semibold text-gray-800 dark:text-white mt-8 mb-4 flex items-center">
            <FileArchive className="h-5 w-5 mr-2" />
            Compression Algorithms
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
              <div className="flex items-center mb-2">
                <FileArchive className="h-5 w-5 text-blue-500 mr-2" />
                <h3 className="font-medium text-lg text-gray-800 dark:text-white">DEFLATE (ZIP)</h3>
              </div>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                A hybrid algorithm combining LZ77 and Huffman coding. It's the standard compression method used in ZIP files,
                offering a good balance between compression ratio and speed. Works well for most file types including text,
                documents, and some media files.
              </p>
              <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                  <Activity className="h-3 w-3 mr-1" />
                  <span>Best for: General purpose compression</span>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
              <div className="flex items-center mb-2">
                <Cpu className="h-5 w-5 text-green-500 mr-2" />
                <h3 className="font-medium text-lg text-gray-800 dark:text-white">Huffman Coding</h3>
              </div>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                An entropy encoding algorithm that uses variable-length codes for different characters. More frequent
                characters get shorter codes, while less frequent ones get longer codes. This method is particularly
                effective for text files with non-uniform character distribution.
              </p>
              <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                  <Activity className="h-3 w-3 mr-1" />
                  <span>Best for: Text files with repeated patterns</span>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
              <div className="flex items-center mb-2">
                <HardDrive className="h-5 w-5 text-purple-500 mr-2" />
                <h3 className="font-medium text-lg text-gray-800 dark:text-white">LZ77</h3>
              </div>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                A dictionary coder that replaces repeated occurrences of data with references to a single copy of that data.
                It uses a sliding window approach to find repeated sequences, making it efficient for files with repeated
                patterns, such as log files or some binary formats.
              </p>
              <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                  <Activity className="h-3 w-3 mr-1" />
                  <span>Best for: Files with repeated sequences</span>
                </div>
              </div>
            </div>
          </div>
          
          <h2 className="text-xl font-semibold text-gray-800 dark:text-white mt-8 mb-4 flex items-center">
            <Code2 className="h-5 w-5 mr-2" />
            How It Works
          </h2>
          
          <div className="space-y-4 mb-8">
            <div className="flex items-start">
              <div className="flex-shrink-0 h-10 w-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                <span className="text-blue-700 dark:text-blue-300 font-medium">1</span>
              </div>
              <div className="ml-4">
                <h3 className="font-medium text-gray-800 dark:text-white">Upload</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Select a file from your device. The file is processed entirely in your browser.
                </p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="flex-shrink-0 h-10 w-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                <span className="text-blue-700 dark:text-blue-300 font-medium">2</span>
              </div>
              <div className="ml-4">
                <h3 className="font-medium text-gray-800 dark:text-white">Process</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Choose to compress or decompress the file. The application will process the file using the selected algorithm.
                </p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="flex-shrink-0 h-10 w-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                <span className="text-blue-700 dark:text-blue-300 font-medium">3</span>
              </div>
              <div className="ml-4">
                <h3 className="font-medium text-gray-800 dark:text-white">Download</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Once processing is complete, download the resulting file to your device.
                </p>
              </div>
            </div>
          </div>
          
          <div className="mt-8">
            <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-4 flex items-center">
              <Users className="h-5 w-5 mr-2" />
              Credits
            </h2>
            
            <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg border border-gray-200 dark:border-gray-600">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-medium text-gray-800 dark:text-white mb-3">Developed by:</h3>
                  <div className="space-y-2">
                    <div className="flex items-center p-3 bg-white dark:bg-gray-800 rounded-md shadow-sm">
                      <div className="h-10 w-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-blue-600 dark:text-blue-300 font-medium">
                        AA
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">Ashna Agarwal</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">2024UCD2165</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center p-3 bg-white dark:bg-gray-800 rounded-md shadow-sm">
                      <div className="h-10 w-10 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center text-green-600 dark:text-green-300 font-medium">
                        YM
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">Yash Mittal</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">2024UCD2113</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-medium text-gray-800 dark:text-white mb-3">Technologies used:</h3>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { name: 'React', description: 'Frontend library', icon: '⚛️' },
                      { name: 'Tailwind CSS', description: 'Styling', icon: '🎨' },
                      { name: 'JSZip', description: 'ZIP compression', icon: '📦' },
                      { name: 'Vite', description: 'Build tool', icon: '⚡' },
                      { name: 'Lucide Icons', description: 'UI Icons', icon: '🎯' },
                      { name: 'Huffman/LZ77', description: 'Algorithms', icon: '🔍' }
                    ].map((tech, index) => (
                      <div key={index} className="p-3 bg-white dark:bg-gray-800 rounded-md shadow-sm">
                        <div className="flex items-center">
                          <span className="text-xl mr-2">{tech.icon}</span>
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">{tech.name}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">{tech.description}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-600">
                <p className="text-center text-xs text-gray-500 dark:text-gray-400">
                  This project was created as part of the Operating Systems Lab course
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;
